import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Groups as GroupsIcon,
  ContentCopy as ContentCopyIcon,
  CreditCard as CardIcon,
  WorkspacePremium as CertificateIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { apiServices } from '../../../services/apiServices';
import Swal from 'sweetalert2';
import imgUrl from '../../../assets/images/imgUrl';
import { jsPDF } from 'jspdf';
import {Configuration} from '../../../assets/data/config';

const EntityActions = ({ selectedAction }) => {
  // Get admin data from localStorage
  const adminDataStr = localStorage.getItem('adminData');
  const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;

  // State for referrals
  const [referralsData, setReferralsData] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsError, setReferralsError] = useState('');
  const [referralsModalOpen, setReferralsModalOpen] = useState(false);
  const [referralsPage, setReferralsPage] = useState(0);
  const [referralsRowsPerPage, setReferralsRowsPerPage] = useState(10);
  const [referralsSearchTerm, setReferralsSearchTerm] = useState('');
  const [referralsPaymentFilter, setReferralsPaymentFilter] = useState('');
  const [referralsActiveFilter, setReferralsActiveFilter] = useState('');
  const [allReferrals, setAllReferrals] = useState([]); // Store all referrals for client-side filtering

  // State for view modal
  const [selectedEntity, setSelectedEntity] = useState(null);
  
  // State for visiting card
  const [visitingCardImage, setVisitingCardImage] = useState(null);
  const [visitingCardLoading, setVisitingCardLoading] = useState(false);
  const [visitingCardError, setVisitingCardError] = useState('');
  const [currentVisitingCardPage, setCurrentVisitingCardPage] = useState(0);

  // State for certificate
  const [certificateImage, setCertificateImage] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState('');
  const [entity, setEntity] = useState('')

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Auto-load visiting card when menu is selected
  useEffect(() => {
    if (selectedAction === 'View Visiting Card' && !visitingCardImage && !visitingCardLoading && !visitingCardError) {
      handleViewCard();
    }
  }, [selectedAction]);

  // Auto-load certificate when menu is selected
  useEffect(() => {
    if (selectedAction === 'View Certificate' && !certificateImage && !certificateLoading && !certificateError) {
      handleViewCertificate();
    }
  }, [selectedAction]);

  // Auto-load referrals when menu is selected
  useEffect(() => {
    if (selectedAction === 'View Referrals' && !referralsData && !referralsLoading && !referralsError) {
      fetchEntityReferrals(0, referralsRowsPerPage);
    }
  }, [selectedAction]);

  // Fetch referrals data
  const fetchEntityReferrals = async (page = 0, limit = 10) => {
    if (!adminData?.peacekeeper_id) {
      setReferralsError('Peacekeeper ID not found');
      return;
    }

    setReferralsLoading(true);
    setReferralsError('');

    try {
      // Fetch all referrals first
      const params = new URLSearchParams({
        page: 1,
        limit: 100 // Get all referrals
      });

      const response = await apiServices.getAPI(`entity/referrals/${adminData.peacekeeper_id}?${params.toString()}`);

      console.log('Referrals API Response:', response.data); // Debug log

      if (response.data.success) {
        setEntity(response.data.data.referrals || [])
        const allReferralsData = response.data.data.referrals || [];
        const originalData = response.data.data; // Preserve original API response data
        setAllReferrals(allReferralsData); // Store all referrals
        
        // Apply filters and pagination
        applyReferralsFiltersAndPagination(allReferralsData, page, limit, originalData);
      } else {
        setReferralsError(response.data.message || 'Failed to fetch referrals');
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      setReferralsError('Error fetching referrals. Please try again.');
    } finally {
      setReferralsLoading(false);
    }
  };

  // Apply filters and pagination to referrals
  const applyReferralsFiltersAndPagination = (referralsList, page = 0, limit = 10, originalData = null) => {
    let filteredReferrals = [...referralsList];
    
    // Apply client-side filtering
    if (referralsSearchTerm) {
      filteredReferrals = filteredReferrals.filter(referral => 
        (referral.name && referral.name.toLowerCase().includes(referralsSearchTerm.toLowerCase())) ||
        (referral.peacekeeperId && referral.peacekeeperId.toLowerCase().includes(referralsSearchTerm.toLowerCase())) ||
        (referral.email && referral.email.toLowerCase().includes(referralsSearchTerm.toLowerCase()))
      );
    }
    
    if (referralsPaymentFilter) {
      filteredReferrals = filteredReferrals.filter(referral => referral.paymentStatus === referralsPaymentFilter);
    }
    
    if (referralsActiveFilter) {
      filteredReferrals = filteredReferrals.filter(referral => referral.activeStatus === referralsActiveFilter);
    }

    // Apply client-side pagination
    const startIndex = page * limit;
    const endIndex = startIndex + limit;
    const paginatedReferrals = filteredReferrals.slice(startIndex, endIndex);

    // Calculate statistics - use original data if available, otherwise calculate from filtered data
    let statistics;
    if (originalData && originalData.statistics && !referralsSearchTerm && !referralsPaymentFilter && !referralsActiveFilter) {
      // Use original statistics if no filters are applied
      statistics = originalData.statistics;
    } else {
      // Calculate statistics from filtered data
      statistics = {
        totalDue: filteredReferrals.reduce((sum, referral) => sum + (referral.totalDue || 0), 0),
        totalReferrals: filteredReferrals.length,
        paidReferrals: filteredReferrals.filter(r => r.paymentStatus === 'COMPLETED').length,
        pendingReferrals: filteredReferrals.filter(r => r.paymentStatus === 'PENDING').length,
        activeReferrals: filteredReferrals.filter(r => r.activeStatus === 'Y').length
      };
    }

    // Update the data with paginated results
    const updatedData = {
      entity: entity,
      referrals: paginatedReferrals,
      statistics: statistics,
      totalReferrals: filteredReferrals.length,
      pagination: {
        startIndex: startIndex,
        endIndex: endIndex,
        total: filteredReferrals.length,
        currentPage: page,
        totalPages: Math.ceil(filteredReferrals.length / limit)
      }
    };

    setReferralsData(updatedData);
  };

  // Handle view referrals
  const handleViewReferrals = () => {
    fetchEntityReferrals(referralsPage, referralsRowsPerPage);
  };

  // Handle copy referral link
  const handleCopyReferralLink = async () => {
    try {
      const referralLink = generateReferralLink(adminData);
      
      await navigator.clipboard.writeText(referralLink);
      
      setSnackbarMessage('Referral link copied to clipboard!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      setSnackbarMessage('Failed to copy link to clipboard');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle view card
  const handleViewCard = async () => {
    setVisitingCardLoading(true);
    setVisitingCardError('');
    
    try {
      const response = await apiServices.getBlobAPI(`visiting-card/${adminData?.userid}`);
      
      if (response.data) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        setVisitingCardImage(blobUrl);
      } else {
        setVisitingCardError('No visiting card data received');
      }
    } catch (error) {
      console.error('Error loading visiting card:', error);
      setVisitingCardError('Failed to load visiting card. Please try again.');
    } finally {
      setVisitingCardLoading(false);
    }
  };

  // Navigate to next visiting card page
  const nextVisitingCardPage = () => {
    setCurrentVisitingCardPage((prev) => (prev + 1) % 4);
  };

  // Navigate to previous visiting card page
  const prevVisitingCardPage = () => {
    setCurrentVisitingCardPage((prev) => (prev - 1 + 4) % 4);
  };

  // Handle download current visiting card page
  const handleDownloadCurrentVisitingCard = async () => {
    try {
      let imageToDownload;
      let filename;
      
      if (currentVisitingCardPage === 0) {
        // Download dynamic card from API
        const response = await apiServices.getBlobAPI(`visiting-card/${adminData?.userid}`);
        if (response.data) {
          imageToDownload = response.data;
          filename = `visiting-card-personalized-${adminData?.name || adminData?.username || 'entity'}.png`;
        } else {
          throw new Error('No personalized card available');
        }
      } else {
        // Download static template card
        const staticCards = [null, imgUrl.visitingCard2, imgUrl.visitingCard3, imgUrl.visitingCard4];
        imageToDownload = staticCards[currentVisitingCardPage];
        filename = `visiting-card-template-${currentVisitingCardPage}-${adminData?.name || adminData?.username || 'entity'}.png`;
      }
      
      if (imageToDownload) {
        const url = window.URL.createObjectURL(imageToDownload);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setSnackbarMessage('Visiting card downloaded successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error downloading visiting card:', error);
      setSnackbarMessage('Failed to download visiting card');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Helper function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate referral link based on usertype and temp_peacekeeper_id
  const generateReferralLink = (entity) => {
    if (!entity) return '';

    const domain = Configuration.frontendDomain;
    const peacekeeperId = entity.peacekeeper_id || entity.temp_peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname, include category in URL
    // Format: domain/register?Name-Category-PeacekeeperID
    if (entity.usertype === 'Entity' && entity.categoryname) {
      const cleanName = (entity.name || entity.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = entity.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    // Format: domain/register?{temp_peacekeeper_id}
    if (entity.temp_peacekeeper_id) {
      return `${domain}/register?${entity.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users (no entity, no temp ID)
    // Format: domain/register?referencenumber={peacekeeper_id}
    return `${domain}/register?referencenumber=${peacekeeperId}`;
  };

  // Handle download all visiting cards as PDF
  const handleDownloadAllVisitingCards = async () => {
    try {
      Swal.fire({
        title: 'Creating PDF',
        html: 'Preparing to generate PDF with all visiting cards...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Define all pages
      const pages = [
        { 
          pageNumber: 0, 
          title: 'Personalized Card', 
          imageUrl: visitingCardImage,
          isDynamic: true 
        },
        { 
          pageNumber: 1, 
          title: 'Template 1', 
          imageUrl: imgUrl.visitingCard2,
          isDynamic: false 
        },
        { 
          pageNumber: 2, 
          title: 'Template 2', 
          imageUrl: imgUrl.visitingCard3,
          isDynamic: false 
        },
        { 
          pageNumber: 3, 
          title: 'Template 3', 
          imageUrl: imgUrl.visitingCard4,
          isDynamic: false 
        }
      ];

      // Filter out pages without image URLs
      const validPages = pages.filter(page => page.imageUrl);

      if (validPages.length === 0) {
        throw new Error('No visiting card images available');
      }

      let pdf = null;
      let firstImageLoaded = false;

      // Process each page
      for (let i = 0; i < validPages.length; i++) {
        const page = validPages[i];
        console.log(`Processing page ${i + 1}: ${page.title}`);
        
        Swal.update({
          html: `Processing ${page.title} (${i + 1}/${validPages.length})...`
        });

        // Fetch image blob
        const imageResponse = await fetch(page.imageUrl);
        if (!imageResponse.ok) {
          throw new Error(`Failed to fetch ${page.title}`);
        }
        const imageBlob = await imageResponse.blob();
        const base64 = await blobToBase64(imageBlob);

        // Load image and process
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            // Calculate high-quality dimensions
            const maxWidth = 1200;
            const maxHeight = 1200;
            
            let finalWidth = img.width;
            let finalHeight = img.height;
            
            if (finalWidth < maxWidth && finalHeight < maxHeight) {
              const scaleX = maxWidth / finalWidth;
              const scaleY = maxHeight / finalHeight;
              const scale = Math.min(scaleX, scaleY, 2);
              
              finalWidth = finalWidth * scale;
              finalHeight = finalHeight * scale;
            }
            
            // Create high-quality canvas
            const canvas = document.createElement('canvas');
            canvas.width = finalWidth;
            canvas.height = finalHeight;
            const ctx = canvas.getContext('2d');
            
            // Enable high-quality rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.webkitImageSmoothingEnabled = true;
            ctx.mozImageSmoothingEnabled = true;
            ctx.msImageSmoothingEnabled = true;
            
            // Draw image
            ctx.drawImage(img, 0, 0, finalWidth, finalHeight);
            
            // Get high-quality base64
            const highQualityBase64 = canvas.toDataURL('image/png', 1.0);
            
            // Create or add page to PDF
            // Use portrait orientation to match visiting card images (325x532 = portrait)
            if (!firstImageLoaded) {
              // Calculate page size from image aspect ratio
              const imageAspectRatio = finalWidth / finalHeight;
              let pageWidth, pageHeight;
              
              if (imageAspectRatio < 1) {
                // Portrait image
                pageWidth = 50.8;  // 2 inches in mm
                pageHeight = 88.9; // 3.5 inches in mm
              } else {
                // Landscape image
                pageWidth = 88.9;  // 3.5 inches in mm
                pageHeight = 50.8; // 2 inches in mm
              }
              
              pdf = new jsPDF({
                orientation: imageAspectRatio < 1 ? "p" : "l",
                unit: "mm",
                format: [pageWidth, pageHeight],
                compress: false
              });
              firstImageLoaded = true;
            } else {
              // Determine orientation for this page
              const imageAspectRatio = finalWidth / finalHeight;
              if (imageAspectRatio < 1) {
                pdf.addPage([50.8, 88.9], "p"); // Portrait
              } else {
                pdf.addPage([88.9, 50.8], "l"); // Landscape
              }
            }
            
            // Calculate scaling - fill entire page
            const currentPageInfo = pdf.internal.pageSize;
            const pageWidth = currentPageInfo.getWidth();
            const pageHeight = currentPageInfo.getHeight();
            
            // Fill entire page without whitespace
            const scaledWidth = pageWidth;
            const scaledHeight = pageHeight;
            const xOffset = 0;
            const yOffset = 0;
            
            // Add image to PDF - fill entire page
            pdf.addImage(highQualityBase64, "PNG", xOffset, yOffset, scaledWidth, scaledHeight, undefined, 'FAST');
            
            resolve();
          };
          img.onerror = () => {
            reject(new Error(`Failed to load image for ${page.title}`));
          };
          img.src = base64;
        });
      }

      if (!pdf || !firstImageLoaded) {
        throw new Error('Failed to create PDF - no valid images found');
      }

      // Generate filename and download
      const peacekeeperId = adminData?.peacekeeper_id || adminData?.username || 'Entity';
      const filename = `VisitingCards-${peacekeeperId}.pdf`;
      
      Swal.update({
        html: 'Downloading PDF...'
      });

      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded!',
        text: `${validPages.length}-page visiting card PDF downloaded successfully`,
        timer: 2000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error creating PDF:', error);
      Swal.fire('Error', error.message || 'Failed to create PDF', 'error');
    }
  };

  // Handle view certificate
  const handleViewCertificate = async () => {
    setCertificateLoading(true);
    setCertificateError('');
    
    try {
      const response = await apiServices.getBlobAPI(`certificate/${adminData?.userid}`);
      
      if (response.data) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        setCertificateImage(blobUrl);
      } else {
        setCertificateError('No certificate data received');
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
      setCertificateError('Failed to load certificate. Please try again.');
    } finally {
      setCertificateLoading(false);
    }
  };

  // Handle download certificate
  const handleDownloadCertificate = async () => {
    try {
      const response = await apiServices.getBlobAPI(`certificate/${adminData?.userid}`);
      
      if (response.data) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `certificate-${adminData?.name || adminData?.username || 'entity'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setSnackbarMessage('Certificate downloaded successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      setSnackbarMessage('Failed to download certificate');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle view entity details
  const handleViewEntityDetails = () => {
    setSelectedEntity(adminData);
    // TODO: Implement view entity details functionality
  };

  // Handle edit entity
  const handleEditEntity = () => {
    setSelectedEntity(adminData);
    // TODO: Implement edit entity functionality
  };

  // Apply referrals filters
  const handleApplyReferralsFilters = () => {
    setReferralsPage(0);
    if (allReferrals.length > 0) {
      // Get original data from current referralsData if available
      const originalData = referralsData && referralsData.statistics ? referralsData : null;
      applyReferralsFiltersAndPagination(allReferrals, 0, referralsRowsPerPage, originalData);
    } else {
      fetchEntityReferrals(0, referralsRowsPerPage);
    }
  };

  // Refresh referrals
  const handleRefreshReferrals = () => {
    fetchEntityReferrals(referralsPage, referralsRowsPerPage);
  };

  // Handle referrals pagination
  const handleReferralsPageChange = (event, newPage) => {
    setReferralsPage(newPage);
    if (allReferrals.length > 0) {
      // Get original data from current referralsData if available
      const originalData = referralsData && referralsData.statistics ? referralsData : null;
      applyReferralsFiltersAndPagination(allReferrals, newPage, referralsRowsPerPage, originalData);
    } else {
      fetchEntityReferrals(newPage, referralsRowsPerPage);
    }
  };

  const handleReferralsRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setReferralsRowsPerPage(newRowsPerPage);
    setReferralsPage(0);
    if (allReferrals.length > 0) {
      // Get original data from current referralsData if available
      const originalData = referralsData && referralsData.statistics ? referralsData : null;
      applyReferralsFiltersAndPagination(allReferrals, 0, newRowsPerPage, originalData);
    } else {
      fetchEntityReferrals(0, newRowsPerPage);
    }
  };

  // Render based on selected action
  const renderContent = () => {
    switch (selectedAction) {
      case 'View Referrals':
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
              Entity Referrals - {adminData?.name || adminData?.username}
            </Typography>

            {referralsLoading && !referralsData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={50} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading referrals...
                  </Typography>
                </Box>
              </Box>
            ) : referralsError && !referralsData ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                  {referralsError}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => fetchEntityReferrals(0, referralsRowsPerPage)}
                  size="medium"
                  startIcon={<GroupsIcon />}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <>

                {/* Filters */}
                <Card sx={{ mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <CardContent sx={{ py: 2, px: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          placeholder="Search by name, email, or peacekeeper ID..."
                          value={referralsSearchTerm}
                          onChange={(e) => setReferralsSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#6c757d' }} />
                              </InputAdornment>
                            ),
                          }}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff'
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={3} md={2}  className="paymentField">
                        <FormControl fullWidth size="small">
                          <InputLabel>Payment Status</InputLabel>
                          <Select
                            value={referralsPaymentFilter}
                            onChange={(e) => setReferralsPaymentFilter(e.target.value)}
                            label="Payment Status"
                            sx={{ backgroundColor: '#fff' }}
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="COMPLETED">Paid</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            <MenuItem value="COMPLIMENTARY">Complimentary</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={3} md={2}  className="paymentField" >
                        <FormControl fullWidth size="small">
                          <InputLabel>Active Status</InputLabel>
                          <Select
                            value={referralsActiveFilter}
                            onChange={(e) => setReferralsActiveFilter(e.target.value)}
                            label="Active Status"
                            sx={{ backgroundColor: '#fff' }}
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Y">Active</MenuItem>
                            <MenuItem value="N">Inactive</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            startIcon={<FilterListIcon />}
                            onClick={handleApplyReferralsFilters}
                            size="small"
                            sx={{
                              backgroundColor: '#214F7D',
                              '&:hover': { backgroundColor: '#1a3f63' },
                              px: 2, py: 1, borderRadius: '6px',
                              textTransform: 'none', fontWeight: 500
                            }}
                          >
                            Apply
                          </Button>
                          <Tooltip title="Refresh">
                            <IconButton onClick={handleRefreshReferrals} size="small"
                              sx={{
                                border: '1px solid #dee2e6',
                                backgroundColor: '#fff',
                                '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#214F7D' }
                              }}
                            >
                              <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Statistics Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }} className="mainReferralgrid">
                  <Grid item xs={12} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }} className='statsReferralcard'>
                      <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        ${referralsData?.statistics?.totalDue?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Entity Due
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', height:'100%' }} className='statsReferralcard'>
                      <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        {referralsData?.statistics?.totalReferrals || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Total Referrals
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e8', border: '1px solid #c8e6c9', height:'100%' }} className='statsReferralcard'>
                      <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        {referralsData?.statistics?.paidReferrals || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Paid
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', height:'100%'}} className='statsReferralcard'>
                      <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                        {referralsData?.statistics?.pendingReferrals || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Pending
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', height:'100%' }} className='statsReferralcard'>
                      <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        {referralsData?.statistics?.activeReferrals || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Active
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                

                {/* Referrals Table */}
                <TableContainer component={Paper} sx={{ maxHeight: 400 }} className='referralsTable'>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>IAPM ID</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Individual Referral</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Individual Register</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Total Due</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Payment</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {referralsLoading ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Box sx={{ py: 3 }}>
                              <CircularProgress size={24} />
                              <Typography variant="body2" sx={{ mt: 1 }}>Loading...</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : referralsData?.referrals && referralsData.referrals.length > 0 ? (
                        referralsData.referrals.map((referral) => (
                          <TableRow key={referral.userId} hover>
                            <TableCell>{referral.name}-{adminData?.categoryname || 'N/A'}-{adminData?.name || adminData?.username || 'N/A'}-{referral.peacekeeperId || 'N/A'}</TableCell>
                            <TableCell>{referral.name || 'N/A'}</TableCell>
                            <TableCell>{referral.country || 'N/A'}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#214F7D' }}>
                                {referral.discountPercent || 0}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {referral.subReferralsCount || 0}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                ${referral.totalDue?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={referral.paymentStatus === 'COMPLETED' ? 'Paid' : referral.paymentStatus === 'COMPLIMENTARY' ? 'COMPLIMENTARY' : referral.paymentStatus || 'N/A'}
                                size="small"
                                sx={{
                                  backgroundColor: referral.paymentStatus === 'COMPLETED' ? '#e8f5e8' : 
                                                  referral.paymentStatus === 'COMPLIMENTARY' ? '#e3f2fd' :
                                                  referral.paymentStatus === 'PENDING' ? '#fff3e0' : '#f5f5f5',
                                  color: referral.paymentStatus === 'COMPLETED' ? '#2e7d32' : 
                                         referral.paymentStatus === 'COMPLIMENTARY' ? '#1976d2' :
                                         referral.paymentStatus === 'PENDING' ? '#f57c00' : '#666',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={referral.activeStatus === 'Y' ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                  backgroundColor: referral.activeStatus === 'Y' ? '#e8f5e8' : '#ffebee',
                                  color: referral.activeStatus === 'Y' ? '#2e7d32' : '#c62828',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              

                              {formatDate(referral.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : referralsError ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Box sx={{ py: 3 }}>
                              <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                                {referralsError}
                              </Typography>
                              <Button 
                                variant="outlined" 
                                onClick={() => fetchEntityReferrals(referralsPage, referralsRowsPerPage)}
                                size="medium"
                              >
                                Retry
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              No referrals found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {referralsData && referralsData.pagination && referralsData.pagination.total > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {referralsData.pagination.startIndex + 1} to {Math.min(referralsData.pagination.endIndex, referralsData.pagination.total)} of {referralsData.pagination.total} entries
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={referralsPage === 0}
                        onClick={() => handleReferralsPageChange(null, referralsPage - 1)}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2" sx={{ px: 2 }}>
                        Page {referralsPage + 1} of {referralsData.pagination.totalPages}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={referralsPage >= referralsData.pagination.totalPages - 1}
                        onClick={() => handleReferralsPageChange(null, referralsPage + 1)}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        );

      case 'Copy Referral Link':
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
              Copy Referral Link
            </Typography>
            {/* <div className='copyReferral'> */}
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your referral link: <strong>{generateReferralLink(adminData)}</strong>
            </Typography>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyReferralLink}
              sx={{ mb: 2 }}
              className='copyClipboardbtn'
            >
              Copy Link to Clipboard
            </Button>
            {/* </div> */}
          </Box>
        );

      case 'View Visiting Card':
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
              Entity Visiting Card
            </Typography>
            <Typography variant="h5" sx={{ color: '#666', mb: 3 }}>
              {adminData?.name || adminData?.username} - {adminData?.categoryname || 'No Category'}
            </Typography>

            {visitingCardLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={50} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading visiting card...
                  </Typography>
                </Box>
              </Box>
            ) : visitingCardError ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                  {visitingCardError}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handleViewCard}
                  size="medium"
                  startIcon={<CardIcon />}
                >
                  Retry
                </Button>
              </Box>
            ) : visitingCardImage ? (
              <Box>
                <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '2px solid #214F7D', mb: 3 }}>
                  
                  {/* Navigation Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, gap: 2 }}>
                    <IconButton
                      onClick={prevVisitingCardPage}
                      disabled={visitingCardLoading}
                      size="small"
                      sx={{
                        border: '1px solid #e0e0e0',
                        '&:hover': { backgroundColor: '#f0f0f0' }
                      }}
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    
                    {/* Page Dots */}
                    {[0, 1, 2, 3].map((page) => (
                      <Box
                        key={page}
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: currentVisitingCardPage === page ? '#3A8139' : '#e0e0e0',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => setCurrentVisitingCardPage(page)}
                      />
                    ))}
                    
                    <IconButton
                      onClick={nextVisitingCardPage}
                      disabled={visitingCardLoading}
                      size="small"
                      sx={{
                        border: '1px solid #e0e0e0',
                        '&:hover': { backgroundColor: '#f0f0f0' }
                      }}
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </Box>

                  {/* Card Image Display */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                    {visitingCardLoading && currentVisitingCardPage === 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={50} />
                        <Typography variant="body2" color="text.secondary">
                          Loading visiting card...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {currentVisitingCardPage === 0 ? (
                          // Dynamic card from API
                          visitingCardImage ? (
                            <Box
                              component="img"
                              src={visitingCardImage}
                              alt="Visiting Card (Personalized)"
                              sx={{
                                maxWidth: '325px',
                                height: '532px',
                                objectFit: 'contain',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                              }}
                            />
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              No personalized visiting card available
                            </Typography>
                          )
                        ) : currentVisitingCardPage === 1 ? (
                          // Static card 2
                          <Box
                            component="img"
                            src={imgUrl.visitingCard2}
                            alt="Visiting Card Template 1"
                            sx={{
                              maxWidth: '325px',
                              height: '532px',
                              objectFit: 'contain',
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                        ) : currentVisitingCardPage === 2 ? (
                          // Static card 3
                          <Box
                            component="img"
                            src={imgUrl.visitingCard3}
                            alt="Visiting Card Template 2"
                            sx={{
                              maxWidth: '325px',
                              height: '532px',
                              objectFit: 'contain',
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                        ) : (
                          // Static card 4
                          <Box
                            component="img"
                            src={imgUrl.visitingCard4}
                            alt="Visiting Card Template 3"
                            sx={{
                              maxWidth: '325px',
                              height: '532px',
                              objectFit: 'contain',
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>

                  {/* Card Type Label */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                    {currentVisitingCardPage === 0 ? 'Personalized Card (Generated)' : `Template ${currentVisitingCardPage}`}
                  </Typography>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCurrentVisitingCard}
                    sx={{ borderColor: '#2e7d32', color: '#2e7d32' }}
                  >
                    Download Current
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAllVisitingCards}
                    sx={{ borderColor: '#f57c00', color: '#f57c00' }}
                  >
                    Download All (PDF)
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Your Entity Visiting Card
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Click the button below to load your visiting card information.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CardIcon />}
                  onClick={handleViewCard}
                  size="large"
                  sx={{
                    backgroundColor: '#214F7D',
                    '&:hover': { backgroundColor: '#1a3f63' }
                  }}
                >
                  Load Visiting Card
                </Button>
              </Box>
            )}
          </Box>
        );

      case 'View Certificate':
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
              Entity Certificate
            </Typography>
            <Typography variant="h5" sx={{ color: '#666', mb: 3 }}>
              {adminData?.name || adminData?.username} - {adminData?.categoryname || 'No Category'}
            </Typography>

            {certificateLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={50} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading certificate...
                  </Typography>
                </Box>
              </Box>
            ) : certificateError ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                  {certificateError}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handleViewCertificate}
                  size="medium"
                  startIcon={<CertificateIcon />}
                >
                  Retry
                </Button>
              </Box>
            ) : certificateImage ? (
              <Box>
                <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '2px solid #214F7D', mb: 3 }}>
                
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={certificateImage}
                      alt="Certificate"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '600px',
                        objectFit: 'contain',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCertificate}
                    sx={{
                      backgroundColor: '#214F7D',
                      '&:hover': { backgroundColor: '#1a3f63' }
                    }}
                  >
                    Download Certificate
                  </Button>
                  
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CertificateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Click the button below to load your certificate.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CertificateIcon />}
                  onClick={handleViewCertificate}
                  size="large"
                  sx={{
                    backgroundColor: '#214F7D',
                    '&:hover': { backgroundColor: '#1a3f63' }
                  }}
                >
                  Load Certificate
                </Button>
              </Box>
            )}
          </Box>
        );

      case 'View Entity Details':
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
              Entity Details
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
              {adminData?.username} ({adminData?.peacekeeper_id})
            </Typography>

            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '2px solid #214F7D' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">IAPM ID</Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: '#214F7D' }}>
                    {adminData?.peacekeeper_id || adminData?.temp_peacekeeper_id || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {adminData?.username || adminData?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {adminData?.email || adminData?.emailid || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {adminData?.mobile || adminData?.phone || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {adminData?.country || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {adminData?.categoryname || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">User Type</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {adminData?.usertype || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(adminData?.created_at)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Additional Information Section */}
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ color: '#214F7D', mb: 2 }}>
                  Additional Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Referral Link</Typography>
                    <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-all', color: '#1976d2' }}>
                      <a
                        href={generateReferralLink(adminData)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        {generateReferralLink(adminData)}
                      </a>
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      #{adminData?.userid || 'N/A'}
                    </Typography>
                  </Grid> */}
                </Grid>
              </Box>
            </Card>            
          </Box>
        );

      case 'Edit Entity':
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
              Edit Entity
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Click the button below to edit your entity information.
            </Typography>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditEntity}
              sx={{ mb: 2 }}
            >
              Edit Entity
            </Button>
          </Box>
        );

      default:
        return (
          <Typography variant="h4" gutterBottom sx={{ mb: 2, color: '#214F7D' }}>
            Select an action from the sidebar
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {renderContent()}


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EntityActions;
